/**
 * DealCustomerCard - Card de cliente formato premium
 * Exibe dados completos do cliente quando o contato está vinculado a um customer
 * 
 * Versão: 1.5.0
 * - Layout premium com avatar grande centralizado
 * - Exibe nome, tipo (PF/PJ), CPF/CNPJ
 * - Seção de contato principal
 * - Barra de atividades rápidas
 * - Ícone para abrir modal de cliente
 * - v1.2.0: Exibe redes sociais (Instagram) na seção de contato principal
 * - v1.3.0: Selo permanente "Cliente" e ações rotuladas sempre visíveis
 *   ("Abrir cliente" / "Editar cliente"), sem depender de hover
 * - v1.5.0: Remove selo interno duplicado "Cliente"; o badge fica único no
 *   header da seção CLIENTE na sidebar (lado direito).
 * 
 * RELEASE: 2026-09-03
 */
import React from 'react';
import { User, Phone, Mail, Building2, FileText, ExternalLink, Pencil } from 'lucide-react';
import { SocialNetworkBadge } from '@/components/ui/social-network-badge';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import { cn } from '@/lib/utils';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { QuickActivityBar, QuickActivityType } from './QuickActivityBar';

// Interface do cliente vinculado ao contato
interface CustomerData {
  id: string;
  nome: string | null;
  nome_fantasia: string | null;
  razao_social: string | null;
  customer_type: 'pf' | 'pj';
  cpf: string | null;
  cnpj: string | null;
}

// Interface do contato
interface ContactData {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
  // v1.2.0: Campos para redes sociais
  channel_ids?: {
    instagram_username?: string;
    instagram_igsid?: string;
    facebook_psid?: string;
    webchat_visitor_id?: string;
  } | null;
  metadata?: Record<string, unknown> | null;
}

// Props do componente
interface DealCustomerCardProps {
  customer: CustomerData;
  contact: ContactData;
  className?: string;
  onViewCustomer?: (customerId: string) => void;
  /** v1.3.0: Abre o cliente já em modo de edição */
  onEditCustomer?: (customerId: string) => void;
  onQuickActivity?: (type: QuickActivityType) => void;
}

/**
 * Retorna o nome de exibição do cliente
 * PF: nome, PJ: nome_fantasia ou razao_social
 */
function getCustomerDisplayName(customer: CustomerData): string {
  if (customer.customer_type === 'pj') {
    return customer.nome_fantasia || customer.razao_social || 'Sem nome';
  }
  return customer.nome || 'Sem nome';
}

/**
 * Retorna o documento do cliente (CPF ou CNPJ)
 */
function getCustomerDocument(customer: CustomerData): string | null {
  if (customer.customer_type === 'pj') {
    return customer.cnpj;
  }
  return customer.cpf;
}

/**
 * Card premium de cliente para o modal de negócio
 */
export function DealCustomerCard({ 
  customer,
  contact,
  className,
  onViewCustomer,
  onEditCustomer,
  onQuickActivity
}: DealCustomerCardProps) {
  const displayName = getCustomerDisplayName(customer);
  const document = getCustomerDocument(customer);
  const isPJ = customer.customer_type === 'pj';

  return (
    <div className={cn('space-y-3', className)}>
      {/* Seção principal do cliente - formato premium */}
      <div className="flex flex-col items-center p-3 rounded-lg bg-muted/30 relative">
        {/* v1.5.0: selo "Cliente" removido deste card; o badge único fica no
            header da seção CLIENTE na sidebar (lado direito), evitando duplicidade. */}

        {/* Avatar grande centralizado */}
        <SmartAvatar 
          src={contact.avatar_url}
          name={displayName}
          size="lg"
          defaultFallback={isPJ ? 'E' : 'C'}
          className="mb-2"
        />
        
        {/* Nome em destaque */}
        <p className="text-sm font-semibold text-center uppercase tracking-wide">
          {displayName}
        </p>
        
        {/* Tipo do cliente */}
        <Badge 
          variant="secondary" 
          className="mt-1 text-[10px] px-2 py-0.5"
        >
          {isPJ ? (
            <span className="flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              Pessoa Jurídica
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              Pessoa Física
            </span>
          )}
        </Badge>
        
        {/* Documento (CPF/CNPJ) */}
        {document && (
          <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
            <FileText className="h-3 w-3" />
            {isPJ ? 'CNPJ' : 'CPF'}: {formatDocumentForDisplay(document)}
          </p>
        )}

        {/* v1.3.0: Ações rotuladas sempre visíveis (abrir / editar cliente) */}
        {(onViewCustomer || onEditCustomer) && (
          <div className="flex items-center gap-2 w-full mt-3">
            {onViewCustomer && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 flex-1 gap-1.5 text-xs"
                onClick={() => onViewCustomer(customer.id)}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Abrir cliente
              </Button>
            )}
            {onEditCustomer && (
              <Button
                variant="default"
                size="sm"
                className="h-8 flex-1 gap-1.5 text-xs"
                onClick={() => onEditCustomer(customer.id)}
              >
                <Pencil className="h-3.5 w-3.5" />
                Editar cliente
              </Button>
            )}
          </div>
        )}

        {/* Barra de atividades rápidas - dentro do card, logo após o CPF */}
        {onQuickActivity && (
          <QuickActivityBar onActivityClick={onQuickActivity} className="mt-3" />
        )}
      </div>

      {/* Seção de contato principal */}
      <div className="space-y-1.5">
        <h5 className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
          Contato Principal
        </h5>
        <div className="p-2 rounded-lg bg-muted/20 space-y-1">
          {/* Nome do contato */}
          <div className="flex items-center gap-2 text-xs">
            <User className="h-3 w-3 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">Nome</span>
            <span className="ml-auto font-medium truncate max-w-[140px]">
              {contact.name || '-'}
            </span>
          </div>
          
          {/* Telefone */}
          <div className="flex items-center gap-2 text-xs">
            <Phone className="h-3 w-3 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">Telefone</span>
            <span className="ml-auto font-medium truncate max-w-[140px]">
              {formatPhoneForDisplay(contact.phone) || '-'}
            </span>
          </div>
          
          {/* Email */}
          <div className="flex items-center gap-2 text-xs">
            <Mail className="h-3 w-3 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">E-mail</span>
            <span className="ml-auto font-medium truncate max-w-[140px]">
              {contact.email || '-'}
            </span>
          </div>
          
          {/* v1.2.0: Redes Sociais */}
          <SocialNetworkBadge 
            channelIds={contact.channel_ids}
            metadata={contact.metadata}
            variant="default"
            showLabel={true}
          />
        </div>
      </div>
    </div>
  );
}
